/**
 * Send Gift Flow - Amount Selection
 * ==================================
 * Step 3: Choose how much to send
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, DollarSign, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendGiftAmountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, recipient } = location.state || {};
  
  const [isDark, setIsDark] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);
  const [membershipLimits, setMembershipLimits] = useState(null);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!category || !recipient) {
      navigate('/send-gift/select');
      return;
    }
    fetchMembershipLimits();
  }, [category, recipient]);

  useEffect(() => {
    const amount = showCustom ? parseInt(customAmount) * 100 : selectedAmount;
    if (amount && amount >= 100) {
      calculateFee(amount);
    } else {
      setFeeInfo(null);
    }
  }, [selectedAmount, customAmount, showCustom]);

  const fetchMembershipLimits = async () => {
    try {
      const res = await fetch(`${API}/card/membership-limits`);
      if (res.ok) {
        setMembershipLimits(await res.json());
      }
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const calculateFee = async (amountCents) => {
    try {
      const res = await fetch(`${API}/gifts/calculate-fee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_cents: amountCents })
      });
      if (res.ok) {
        setFeeInfo(await res.json());
      }
    } catch (error) {
      console.error('Error calculating fee:', error);
    }
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37',
    gold: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const suggestedAmounts = category?.suggestedAmounts || [100, 250, 500, 1000, 2500];
  const minAmount = category?.minAmount || 2000;
  const maxAmount = Math.min(
    category?.maxAmount || 500000,
    membershipLimits?.max_gift_cents || 500000
  );

  const getCurrentAmount = () => {
    if (showCustom && customAmount) {
      return parseInt(customAmount) * 100;
    }
    return selectedAmount;
  };

  const isValidAmount = () => {
    const amount = getCurrentAmount();
    if (!amount) return false;
    return amount >= minAmount && amount <= maxAmount;
  };

  const handleContinue = () => {
    const amount = getCurrentAmount();
    if (isValidAmount()) {
      navigate('/send-gift/message', {
        state: {
          category,
          recipient,
          amountCents: amount,
          feeInfo
        }
      });
    }
  };

  const handleAmountSelect = (amount) => {
    setShowCustom(false);
    setSelectedAmount(amount * 100);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '200px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.text
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Send a Gift
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '13px', 
            margin: '4px 0 0 0' 
          }}>
            Step 3 of 5 • Choose amount
          </p>
        </div>
      </div>

      {/* Step Progress */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '6px'
        }}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: step <= 3 ? colors.gold : 'rgba(138, 115, 85, 0.2)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Category & Recipient Summary */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          background: 'rgba(215, 181, 109, 0.08)',
          borderRadius: '14px',
          border: '1px solid rgba(215, 181, 109, 0.2)'
        }}>
          <span style={{ fontSize: '28px' }}>{category?.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: colors.text, fontSize: '15px', fontWeight: '600' }}>
              {category?.name} for {recipient?.name}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '12px' }}>
              {formatCurrency(minAmount)} – {formatCurrency(maxAmount)}
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      <div style={{ padding: '0 20px 24px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '24px',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          How much?
        </h2>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          {recipient?.name} will receive the full gift amount
        </p>
      </div>

      {/* Suggested Amounts */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px'
        }}>
          {suggestedAmounts.filter(a => a * 100 <= maxAmount).map((amount) => {
            const isSelected = !showCustom && selectedAmount === amount * 100;
            return (
              <button
                key={amount}
                onClick={() => handleAmountSelect(amount)}
                data-testid={`amount-${amount}`}
                style={{
                  padding: '18px 12px',
                  borderRadius: '14px',
                  border: isSelected ? '2px solid #D7B56D' : `1px solid ${colors.border}`,
                  background: isSelected ? 'rgba(215, 181, 109, 0.15)' : colors.cardBg,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: isSelected ? colors.accent : colors.text,
                  fontFamily: 'Cinzel, serif'
                }}>
                  ${amount.toLocaleString()}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Amount */}
      <div style={{ padding: '0 16px 24px' }}>
        <button
          onClick={() => {
            setShowCustom(true);
            setSelectedAmount(null);
          }}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '14px',
            border: showCustom ? '2px solid #D7B56D' : `1px solid ${colors.border}`,
            background: showCustom ? 'rgba(215, 181, 109, 0.15)' : colors.cardBg,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          {showCustom ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={24} color={colors.accent} />
              <input
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                autoFocus
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: colors.accent,
                  fontFamily: 'Cinzel, serif'
                }}
              />
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px'
            }}>
              <DollarSign size={18} color={colors.textMuted} />
              <span style={{ color: colors.textMuted, fontSize: '15px' }}>
                Custom Amount
              </span>
            </div>
          )}
        </button>
      </div>

      {/* Fee Breakdown */}
      {feeInfo && isValidAmount() && (
        <div style={{ padding: '0 16px 24px' }}>
          <div style={{
            background: colors.cardBg,
            borderRadius: '14px',
            border: `1px solid ${colors.border}`,
            padding: '16px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <span style={{ color: colors.textMuted, fontSize: '14px' }}>Gift Amount</span>
              <span style={{ color: colors.text, fontSize: '14px', fontWeight: '600' }}>
                {feeInfo.amount_display}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: `1px solid ${colors.border}`
            }}>
              <span style={{ 
                color: colors.textMuted, 
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                Service Fee
                <Info size={14} color={colors.textMuted} />
              </span>
              <span style={{ color: colors.textMuted, fontSize: '14px' }}>
                {feeInfo.fee_display}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between'
            }}>
              <span style={{ color: colors.text, fontSize: '16px', fontWeight: '700' }}>Total</span>
              <span style={{ color: colors.accent, fontSize: '18px', fontWeight: '700', fontFamily: 'Cinzel, serif' }}>
                {feeInfo.total_display}
              </span>
            </div>
            <div style={{ 
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#4CAF50', fontSize: '13px', fontWeight: '600' }}>
                {recipient?.name} receives {feeInfo.amount_display}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Continue Button */}
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: 0,
        right: 0,
        padding: '16px',
        background: isDark 
          ? 'linear-gradient(180deg, transparent 0%, #0D0D0D 30%)'
          : 'linear-gradient(180deg, transparent 0%, #FAFAFA 30%)'
      }}>
        <button
          onClick={handleContinue}
          disabled={!isValidAmount()}
          data-testid="continue-btn"
          style={{
            width: '100%',
            padding: '16px',
            background: isValidAmount() ? colors.gold : 'rgba(138, 115, 85, 0.3)',
            border: 'none',
            borderRadius: '14px',
            color: isValidAmount() ? '#2C1B13' : colors.textMuted,
            fontSize: '16px',
            fontWeight: '700',
            cursor: isValidAmount() ? 'pointer' : 'not-allowed',
            boxShadow: isValidAmount() ? '0 4px 20px rgba(215, 181, 109, 0.4)' : 'none'
          }}
        >
          Continue
        </button>
        
        {membershipLimits && (
          <p style={{
            textAlign: 'center',
            color: colors.textMuted,
            fontSize: '11px',
            marginTop: '12px'
          }}>
            {membershipLimits.tier_name} limit: {formatCurrency(membershipLimits.max_gift_cents)} per gift
          </p>
        )}
      </div>
    </div>
  );
};

export default SendGiftAmountPage;
